/**
 * Engine.ts — wires state + loop + save + event bus together and exposes the
 * action API the UI calls. Keeps all timing/clock concerns in one place.
 */
import { type GameState } from './GameState';
import { EventBus } from './EventBus';
import { systemClock, type Clock } from './clock';
import { GameLoop, runCatchUp, type TickContext } from './GameLoop';
import { SaveManager, MemoryStorage, type StorageLike } from './SaveManager';
import { computeDerived, type Derived } from '../systems/derived';
import { tickGame } from '../systems/tick';

export interface EngineOptions {
  storage?: StorageLike;
  clock?: Clock;
}

export interface OfflineSummary {
  ms: number;
  moneyGained: number;
  oreGained: number;
  cashDelta: number;
}

export interface InitResult {
  offlineMs: number;
  fresh: boolean;
  offlineSummary: OfflineSummary;
}

function resolveStorage(opt?: StorageLike): StorageLike {
  if (opt) return opt;
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* access can throw in sandboxed contexts */
  }
  return new MemoryStorage();
}

export class Engine {
  readonly bus = new EventBus();
  readonly clock: Clock;
  readonly save: SaveManager;
  state!: GameState;
  private loop!: GameLoop;
  private ctx: TickContext;
  private lastAutosave = 0;

  constructor(opts: EngineOptions = {}) {
    this.clock = opts.clock ?? systemClock;
    this.save = new SaveManager(resolveStorage(opts.storage));
    this.ctx = { bus: this.bus, clock: this.clock };
  }

  /** Load (or start) the game, credit offline progress, and begin the live loop. */
  init(onFrame?: () => void): InitResult {
    const now = this.clock.now();
    const { state, offlineMs, fresh } = this.save.load(now);
    this.state = state;

    // Snapshot to summarise offline gains for the welcome-back modal.
    const before = { money: state.economy.money, ore: state.stats.totalOreMined, life: state.stats.lifetimeEarnings };
    if (offlineMs > 0) runCatchUp(this.state, offlineMs, tickGame, this.ctx);
    const offlineSummary = {
      ms: offlineMs,
      moneyGained: Math.max(0, state.stats.lifetimeEarnings - before.life),
      oreGained: Math.max(0, state.stats.totalOreMined - before.ore),
      cashDelta: state.economy.money - before.money,
    };

    this.loop = new GameLoop(this.state, tickGame, this.ctx, () => {
      this.maybeAutosave();
      onFrame?.();
    });
    this.lastAutosave = now;
    if (!fresh) this.bus.emit('load', { offlineMs });
    return { offlineMs, fresh, offlineSummary };
  }

  start(): void {
    this.loop.start();
  }
  stop(): void {
    this.loop?.stop();
  }

  /** Advance the simulation manually (used by tests / headless drivers). */
  advance(realMs: number): number {
    return this.loop.advance(realMs);
  }

  derived(): Derived {
    return computeDerived(this.state);
  }

  private maybeAutosave(): void {
    const now = this.clock.now();
    if (now - this.lastAutosave >= 20_000) {
      this.saveNow(false);
      this.lastAutosave = now;
    }
  }

  saveNow(manual = true): void {
    this.save.save(this.state, this.clock.now());
    this.bus.emit('save', { manual });
  }

  /**
   * Replace the live state IN PLACE so the GameLoop (which holds a reference to
   * the original object) keeps advancing the same object the UI reads. Used by
   * save import.
   */
  replaceState(next: GameState): void {
    // Clear keys that may be absent in `next`, then copy everything over.
    for (const k of Object.keys(this.state)) delete (this.state as any)[k];
    Object.assign(this.state, next);
  }

  hardReset(): void {
    this.save.wipe();
    this.bus.emit('reset', {});
  }
}
